/**
 * Annotations API client — measurements (Strecke / Fläche / Notiz) persisted
 * per project. Backend: `Gontec-Mobile-Django/annotations/`.
 *
 * Endpoint contract:
 *   GET    /api/projects/<id>/annotations/                → { items: ServerAnnotation[] }
 *   POST   /api/projects/<id>/annotations/                → ServerAnnotation (201 or 200 if idempotent hit)
 *   DELETE /api/projects/<id>/annotations/<aid>/          → 204 (or 404 if already gone)
 *
 * Goes through `authenticatedFetch` which adds the JWT header and
 * auto-refreshes on 401.
 */

import { authenticatedFetch } from '../auth/webAuthClient'

export type AnnotationKind = 'distance' | 'area' | 'note'

export interface AnnotationPoint {
  x: number
  y: number
  z: number
}

export interface AnnotationAuthor {
  id: string
  email: string
  first_name: string
  last_name: string
}

export interface ServerAnnotation {
  id: string
  project_id: string
  kind: AnnotationKind
  points: AnnotationPoint[]
  color: number
  label: string
  text: string
  created_by: AnnotationAuthor | null
  created_at: string
  updated_at: string
}

export interface CreateAnnotationPayload {
  id: string
  kind: AnnotationKind
  points: AnnotationPoint[]
  color: number
  label: string
  text?: string
}

export async function listAnnotations(projectId: string): Promise<ServerAnnotation[]> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/annotations/`,
  )
  if (!resp.ok) {
    throw new Error(`Failed to list annotations: HTTP ${resp.status}`)
  }
  const data = (await resp.json()) as { items?: ServerAnnotation[] }
  return data.items ?? []
}

export async function createAnnotation(
  projectId: string,
  payload: CreateAnnotationPayload,
): Promise<ServerAnnotation> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/annotations/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  )
  if (!resp.ok) {
    throw new Error(`Failed to create annotation: HTTP ${resp.status}`)
  }
  return (await resp.json()) as ServerAnnotation
}

/**
 * Delete returns 204 on success or 404 if already gone (treated as success
 * by callers — race with another user is OK).
 */
export async function deleteAnnotation(projectId: string, id: string): Promise<void> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(projectId)}/annotations/${encodeURIComponent(id)}/`,
    { method: 'DELETE' },
  )
  if (resp.status === 204 || resp.status === 404) return
  throw new Error(`Failed to delete annotation: HTTP ${resp.status}`)
}
